/**
 * The base-game tile pack: 24 distinct tile types, 72 tiles. [P1-tiles]
 *
 * Authored against `.planning/research/rules-canon.md`. Where that document and the legacy repo's
 * `future-logic.md` disagree, the canon wins ([D-15]).
 *
 * ── How to read a tile ───────────────────────────────────────────────────────────────────────────
 *
 * `edges` is [N, E, S, W]. Sub-edges run clockwise from the north-west corner:
 *     N = 0,1,2 (west→east)   E = 3,4,5 (north→south)   S = 6,7,8 (east→west)   W = 9,10,11 (south→north)
 * A road side owns only its CENTRE sub-edge; the two outer ones are field, and they are separate
 * fields, because a road divides a field. See `types.ts` for the full diagram.
 *
 * `features` is authored, never inferred ([D-11]) — in particular whether two city edges are joined
 * *inside* the tile is a property of the artwork, not something derivable from the edge list. Tiles F/G
 * (opposite cities joined) and H (opposite cities separate) have identical `edges` and different
 * `features`; that is the whole reason this is authored.
 *
 * `adjacentCities` on a field lists the indices of city features that field touches. Farm scoring at
 * the end of the game is exactly a walk over these.
 *
 * Letters A–X follow the standard community labelling, so this table can be diffed against any
 * published distribution.
 */
const F = 'field';
const C = 'city';
const R = 'road';
export const BASE_TILES = [
    // ── Cloisters ───────────────────────────────────────────────────────────────────────────────────
    {
        id: 'A',
        pack: 'base',
        copies: 2,
        description: 'Cloister with a road leading south',
        edges: [F, F, R, F],
        features: [
            { kind: 'cloister', subEdges: [] },
            { kind: 'road', subEdges: [7] },
            // The road dead-ends at the cloister, so it does NOT divide the field — the field wraps around
            // the end of it. A road only splits a field when it crosses from edge to edge.
            { kind: 'field', subEdges: [0, 1, 2, 3, 4, 5, 6, 8, 9, 10, 11], adjacentCities: [] },
        ],
    },
    {
        id: 'B',
        pack: 'base',
        copies: 4,
        description: 'Cloister alone in a field',
        edges: [F, F, F, F],
        features: [
            { kind: 'cloister', subEdges: [] },
            { kind: 'field', subEdges: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], adjacentCities: [] },
        ],
    },
    // ── Cities ──────────────────────────────────────────────────────────────────────────────────────
    {
        id: 'C',
        pack: 'base',
        copies: 1,
        description: 'City on all four sides, with a pennant',
        edges: [C, C, C, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11], pennant: true },
        ],
    },
    {
        id: 'D',
        pack: 'base',
        copies: 4,
        isStart: true,
        description: 'City to the north, road running east-west (the starting tile)',
        edges: [C, R, F, R],
        features: [
            { kind: 'city', subEdges: [0, 1, 2] },
            { kind: 'road', subEdges: [4, 10] },
            // The road crosses the tile, so the field north of it (touching the city) and the field south of
            // it are two different farms.
            { kind: 'field', subEdges: [3, 11], adjacentCities: [0] },
            { kind: 'field', subEdges: [5, 6, 7, 8, 9], adjacentCities: [] },
        ],
    },
    {
        id: 'E',
        pack: 'base',
        copies: 5,
        description: 'City to the north, open field otherwise',
        edges: [C, F, F, F],
        features: [
            { kind: 'city', subEdges: [0, 1, 2] },
            { kind: 'field', subEdges: [3, 4, 5, 6, 7, 8, 9, 10, 11], adjacentCities: [0] },
        ],
    },
    {
        id: 'F',
        pack: 'base',
        copies: 2,
        description: 'City spanning north and south, joined, with a pennant',
        edges: [C, F, C, F],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 6, 7, 8], pennant: true },
            // The city cuts the tile in half, so east and west are separate farms.
            { kind: 'field', subEdges: [3, 4, 5], adjacentCities: [0] },
            { kind: 'field', subEdges: [9, 10, 11], adjacentCities: [0] },
        ],
    },
    {
        id: 'G',
        pack: 'base',
        copies: 1,
        description: 'City spanning north and south, joined, no pennant',
        edges: [C, F, C, F],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 6, 7, 8] },
            { kind: 'field', subEdges: [3, 4, 5], adjacentCities: [0] },
            { kind: 'field', subEdges: [9, 10, 11], adjacentCities: [0] },
        ],
    },
    {
        id: 'H',
        pack: 'base',
        copies: 3,
        description: 'Two separate cities, north and south',
        edges: [C, F, C, F],
        features: [
            { kind: 'city', subEdges: [0, 1, 2] },
            { kind: 'city', subEdges: [6, 7, 8] },
            // Unlike F/G the cities are NOT joined, so the field runs between them and east meets west.
            { kind: 'field', subEdges: [3, 4, 5, 9, 10, 11], adjacentCities: [0, 1] },
        ],
    },
    {
        id: 'I',
        pack: 'base',
        copies: 2,
        description: 'Two separate cities, east and south',
        edges: [F, C, C, F],
        features: [
            { kind: 'city', subEdges: [3, 4, 5] },
            { kind: 'city', subEdges: [6, 7, 8] },
            { kind: 'field', subEdges: [9, 10, 11, 0, 1, 2], adjacentCities: [0, 1] },
        ],
    },
    {
        id: 'J',
        pack: 'base',
        copies: 3,
        description: 'City to the north, road curving from east to south',
        edges: [C, R, R, F],
        features: [
            { kind: 'city', subEdges: [0, 1, 2] },
            { kind: 'road', subEdges: [4, 7] },
            { kind: 'field', subEdges: [3, 8, 9, 10, 11], adjacentCities: [0] },
            { kind: 'field', subEdges: [5, 6], adjacentCities: [] },
        ],
    },
    {
        id: 'K',
        pack: 'base',
        copies: 3,
        description: 'City to the north, road curving from west to south',
        edges: [C, F, R, R],
        features: [
            { kind: 'city', subEdges: [0, 1, 2] },
            { kind: 'road', subEdges: [7, 10] },
            { kind: 'field', subEdges: [3, 4, 5, 6, 11], adjacentCities: [0] },
            { kind: 'field', subEdges: [8, 9], adjacentCities: [] },
        ],
    },
    {
        id: 'L',
        pack: 'base',
        copies: 3,
        description: 'City to the north, three roads meeting below it',
        edges: [C, R, R, R],
        features: [
            { kind: 'city', subEdges: [0, 1, 2] },
            // Three roads, each ending at the junction — three separate roads, not one.
            { kind: 'road', subEdges: [4] },
            { kind: 'road', subEdges: [7] },
            { kind: 'road', subEdges: [10] },
            // THREE field regions, not four. The strip of field beneath the city wall runs the width of the
            // tile, so the sliver east of the junction and the sliver west of it are the same farm.
            // (Confirmed by the 126-field-region total — see the test in base.test.ts.)
            { kind: 'field', subEdges: [3, 11], adjacentCities: [0] },
            { kind: 'field', subEdges: [5, 6], adjacentCities: [] },
            { kind: 'field', subEdges: [8, 9], adjacentCities: [] },
        ],
    },
    {
        id: 'M',
        pack: 'base',
        copies: 2,
        description: 'City wrapping the north-west corner, with a pennant',
        edges: [C, F, F, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 9, 10, 11], pennant: true },
            { kind: 'field', subEdges: [3, 4, 5, 6, 7, 8], adjacentCities: [0] },
        ],
    },
    {
        id: 'N',
        pack: 'base',
        copies: 3,
        description: 'City wrapping the north-west corner, no pennant',
        edges: [C, F, F, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 9, 10, 11] },
            { kind: 'field', subEdges: [3, 4, 5, 6, 7, 8], adjacentCities: [0] },
        ],
    },
    {
        id: 'O',
        pack: 'base',
        copies: 2,
        description: 'City wrapping the north-west corner with a road below, with a pennant',
        edges: [C, R, R, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 9, 10, 11], pennant: true },
            { kind: 'road', subEdges: [4, 7] },
            { kind: 'field', subEdges: [3, 8], adjacentCities: [0] },
            { kind: 'field', subEdges: [5, 6], adjacentCities: [] },
        ],
    },
    {
        id: 'P',
        pack: 'base',
        copies: 3,
        description: 'City wrapping the north-west corner with a road below, no pennant',
        edges: [C, R, R, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 9, 10, 11] },
            { kind: 'road', subEdges: [4, 7] },
            { kind: 'field', subEdges: [3, 8], adjacentCities: [0] },
            { kind: 'field', subEdges: [5, 6], adjacentCities: [] },
        ],
    },
    {
        id: 'Q',
        pack: 'base',
        copies: 1,
        description: 'City on three sides, with a pennant',
        edges: [C, C, F, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 3, 4, 5, 9, 10, 11], pennant: true },
            { kind: 'field', subEdges: [6, 7, 8], adjacentCities: [0] },
        ],
    },
    {
        id: 'R',
        pack: 'base',
        copies: 3,
        description: 'City on three sides, no pennant',
        edges: [C, C, F, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 3, 4, 5, 9, 10, 11] },
            { kind: 'field', subEdges: [6, 7, 8], adjacentCities: [0] },
        ],
    },
    {
        id: 'S',
        pack: 'base',
        copies: 2,
        description: 'City on three sides with a road leaving south, with a pennant',
        edges: [C, C, R, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 3, 4, 5, 9, 10, 11], pennant: true },
            { kind: 'road', subEdges: [7] },
            // The road runs from the city gate to the south edge, splitting the sliver of field in two.
            { kind: 'field', subEdges: [6], adjacentCities: [0] },
            { kind: 'field', subEdges: [8], adjacentCities: [0] },
        ],
    },
    {
        id: 'T',
        pack: 'base',
        copies: 1,
        description: 'City on three sides with a road leaving south, no pennant',
        edges: [C, C, R, C],
        features: [
            { kind: 'city', subEdges: [0, 1, 2, 3, 4, 5, 9, 10, 11] },
            { kind: 'road', subEdges: [7] },
            { kind: 'field', subEdges: [6], adjacentCities: [0] },
            { kind: 'field', subEdges: [8], adjacentCities: [0] },
        ],
    },
    // ── Roads ───────────────────────────────────────────────────────────────────────────────────────
    {
        id: 'U',
        pack: 'base',
        copies: 8,
        description: 'Straight road, north to south',
        edges: [R, F, R, F],
        features: [
            { kind: 'road', subEdges: [1, 7] },
            { kind: 'field', subEdges: [2, 3, 4, 5, 6], adjacentCities: [] },
            { kind: 'field', subEdges: [8, 9, 10, 11, 0], adjacentCities: [] },
        ],
    },
    {
        id: 'V',
        pack: 'base',
        copies: 9,
        description: 'Road curving from west to south',
        edges: [F, F, R, R],
        features: [
            { kind: 'road', subEdges: [7, 10] },
            { kind: 'field', subEdges: [8, 9], adjacentCities: [] },
            { kind: 'field', subEdges: [11, 0, 1, 2, 3, 4, 5, 6], adjacentCities: [] },
        ],
    },
    {
        id: 'W',
        pack: 'base',
        copies: 4,
        description: 'Three roads meeting (east, south, west)',
        edges: [F, R, R, R],
        features: [
            { kind: 'road', subEdges: [4] },
            { kind: 'road', subEdges: [7] },
            { kind: 'road', subEdges: [10] },
            { kind: 'field', subEdges: [11, 0, 1, 2, 3], adjacentCities: [] },
            { kind: 'field', subEdges: [5, 6], adjacentCities: [] },
            { kind: 'field', subEdges: [8, 9], adjacentCities: [] },
        ],
    },
    {
        id: 'X',
        pack: 'base',
        copies: 1,
        description: 'Crossroads — four roads meeting',
        edges: [R, R, R, R],
        features: [
            { kind: 'road', subEdges: [1] },
            { kind: 'road', subEdges: [4] },
            { kind: 'road', subEdges: [7] },
            { kind: 'road', subEdges: [10] },
            { kind: 'field', subEdges: [2, 3], adjacentCities: [] },
            { kind: 'field', subEdges: [5, 6], adjacentCities: [] },
            { kind: 'field', subEdges: [8, 9], adjacentCities: [] },
            { kind: 'field', subEdges: [11, 0], adjacentCities: [] },
        ],
    },
];
/** The tile the board starts with. */
export const START_TILE_ID = 'D';
//# sourceMappingURL=base.js.map